import React, { Suspense, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Await, useAsyncValue, useFetchers, useLoaderData, useLocation } from 'react-router-dom'

import DeleteCollectionDialog from '@components/myScores/DeleteCollectionDialog'
import NFIcon from '@components/shared/NFIcon'
import Tooltip from '@components/shared/Tooltip'
import MustBePremiumDialog from '@components/shared/dialogs/MustBePremiumDialog'
import CollectionFormDialog from '@components/shared/dialogs/collection/CollectionFormDialog'
import MenuItem from '@layout/MenuItem'
import { useDialogStore } from '@stores/dialog.store'
import { useSession } from '@stores/globalData/globalData.store'
import PageRoutes from '@utils/page-routes'

import type Collection from '@models/Collection'
import type { CollectionResponse } from '@pages/home/actions/collection/addCollection.action'
import type {
  HomeLoaderResponse,
  PrivateCollectionsResponse,
} from '@pages/home/loaders/home.loader'

const CollectionsMenu: React.FC = () => {
  const { t } = useTranslation()
  const openDialog = useDialogStore((s) => s.openDialog)
  const session = useSession()

  const data = useLoaderData() as HomeLoaderResponse

  const onClick = (): void => {
    if (session.hasPremiumFeatures()) {
      openDialog('add_collection')
    } else {
      openDialog('require_premium_collections')
    }
  }

  return (
    <>
      <button
        type="button"
        className="flex flex-row px-1.5 mt-2.5 items-center mb-2 text-left"
        onClick={onClick}>
        <p className="font-medium flex-1">{t('titles.collections')}</p>
        <NFIcon icon="add-sign" className="text-md orange-link" />
        {/*className="flex flex-row items-center orange-link" onClick={onClick}>*/}
      </button>
      <Suspense fallback={null}>
        <Await resolve={data.collections}>
          <CollectionList />
        </Await>
      </Suspense>
      <CollectionFormDialog standalone />
      <MustBePremiumDialog
        title={t('action.addToCollection')}
        action={t('action.addToCollection')}
        name="require_premium_collections"
      />
      <DeleteCollectionDialog />
    </>
  )
}

export default CollectionsMenu

const CollectionList: React.FC = () => {
  const { pathname } = useLocation()
  const collectionData = useAsyncValue() as PrivateCollectionsResponse

  const fetchers = useFetchers()
  const addColFetcher = fetchers.find((fetcher) => fetcher.formAction === '/home/addCollection')

  // This is only split into its own state to allow for optimistic UI
  const [collections, setCollections] = useState(collectionData.collections)

  // Update collections state when we receive a new update
  useEffect(() => {
    setCollections(collectionData.collections)
  }, [collectionData.collections])

  // Optimistically update collection list if add collection fetcher succeeded. This code is not
  // strictly required, but it will make the collection list UI updates quicker.
  useEffect(() => {
    if (!addColFetcher?.data) return

    const { collection: newCollection } = addColFetcher.data as CollectionResponse
    if (newCollection) {
      setCollections((prevCollections) => [...prevCollections, newCollection])
    }
  }, [addColFetcher?.data])

  return (
    <>
      {collections.map((collection) => (
        <MenuItem
          key={`collection-${collection.id}`}
          label={collection.name}
          to={PageRoutes.homeCollectionPath(collection.id)}
          icon={<CollectionIcon collection={collection} />}
          rightContent={collection.document_count}
          selected={pathname === PageRoutes.homeCollectionPath(collection.id)}
          className="mb-1"
        />
      ))}
    </>
  )
}

interface CollectionIconProps {
  collection: Collection
}
const CollectionIcon: React.FC<CollectionIconProps> = ({ collection }) => {
  const openDialog = useDialogStore((s) => s.openDialog)
  const [hover, setHover] = useState(false)
  const [showTippy, setShowTippy] = useState(false)
  return (
    <Tooltip
      visible={showTippy}
      onHide={() => setShowTippy(false)}
      onClickOutside={() => setShowTippy(false)}
      interactive
      arrow
      placement="bottom"
      content={
        <div className="text-sm py-1">
          <button
            type="button"
            className="block cursor-pointer mb-2 hover:opacity-90"
            onClick={(e) => {
              e.preventDefault()
              openDialog('confirm_delete_collection', { state: collection })
              setShowTippy(false)
            }}>
            Delete Collection
          </button>
          <button
            type="button"
            className="block cursor-pointer hover:opacity-90"
            onClick={(e) => {
              e.preventDefault()
              openDialog('add_collection', { state: { collection } })
              setShowTippy(false)
            }}>
            Edit Details
          </button>
        </div>
      }>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          setShowTippy(true)
        }}
        onMouseOver={() => setHover(true)}
        onMouseOut={() => setHover(false)}>
        <NFIcon icon={hover || showTippy ? 'folder-alt' : 'folder'} className="text-xl" />
      </button>
    </Tooltip>
  )
}
